import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { Form, Formik, FormikHelpers } from 'formik';
import merge from 'lodash.merge';
import { DateTime } from 'luxon';

import Typography from '@material-ui/core/Typography';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import Collapse from '@material-ui/core/Collapse';
import Link from '@material-ui/core/Link';
import DropDownIcon from '@material-ui/icons/ArrowDropDownCircle';
import TextField from '@material-ui/core/TextField';

import { getYearsAndMonthsFromLuxon, sqlDateToYearAndMonth } from 'helpers/helpers';
import compose from 'helpers/compose';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';
import withoutEnterSubmit from 'components/withoutEnterSubmit';
import ControlButtons from 'components/ControlButtons';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import FormTextField from 'components/FormFields/FormTextField';
import FormNumberField from 'components/FormFields/FormNumberField';
import FormSelectField from 'components/FormFields/FormSelectField';
import FormDateSelects from 'components/FormFields/FormDateSelects';
import Switch from 'components/FormFields/Switch';
import {
  Reserve,
  ReservesOptions,
  getInitialValues,
  Horizon,
} from 'stores/workflowStores/finances/newReservesStore/model';
import { Currency } from 'enums';
import { ReservesStore } from 'stores/workflowStores/finances/newReservesStore/reservesStore';
import useStyles from './styles/moduleStyles';

const FormWithoutEnterSubmit = withoutEnterSubmit(Form);

interface OwnProps {
  reserveId: string;
  opened: boolean;
  handleClose: (showChangeDialog?: boolean) => void;
  currency: Currency;
  refresh: () => void;
  options: ReservesOptions;
  openRent?: (open: boolean) => void;
  originalReserve?: Reserve;
}

interface Stores {
  reservesStore: ReservesStore;
}

type Props = OwnProps & Stores;

const ReserveModal: React.FC<Props> = ({
  reserveId,
  opened,
  handleClose,
  reservesStore,
  currency,
  refresh,
  options,
  openRent,
  originalReserve,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  let formSubmitting = false;
  const formKey = 'ReserveForm';
  const { fetchReserve, handleSubmit, data } = reservesStore;
  const [rentDialogOpen, setRentDialogOpen] = useState(false);
  const [noNewDepositsDialogOpen, setNoNewDepositsDialogOpen] = useState(false);
  const [showDetail, setShowDetail] = useState(false);

  useEffect(() => {
    if (opened && reserveId) {
      fetchReserve(reserveId);
    }
  }, [reserveId, fetchReserve, opened]);

  const onSubmit = async (values: Reserve, formikHelpers: FormikHelpers<Reserve>) => {
    const ok = await handleSubmit(values, formikHelpers);
    if (ok) {
      await refresh();
      handleClose(true);
      return true;
    } else {
      return false;
    }
  };

  const checkIfRentNeeded = (values: Reserve, formikHelpers: FormikHelpers<Reserve>) => {
    if (values.horizon === Horizon.RENT_START && openRent) {
      setRentDialogOpen(true);
    } else {
      onSubmit(values, formikHelpers);
    }
  };

  const translatedCurrency = useMemo(() => t(`currency_${currency}`), [currency, t]);

  const horizonOptions = useMemo(
    () =>
      options
        ? options.horizon.map((option: string) => ({
            value: option,
            label: t([`options_horizon_reserves_${option}`, `options_horizon_${option}`]),
          }))
        : [],
    [options, t],
  );

  const minDate = useMemo(() => getYearsAndMonthsFromLuxon(DateTime.local().plus({ months: 1 })), []);
  const maxDate = useMemo(() => sqlDateToYearAndMonth(options?.maxDate), [options]);

  const initialValues = getInitialValues(currency);

  return (
    <>
      <Dialog maxWidth="md" open={opened}>
        <DialogHeader onClose={handleClose}>{t('finQuestionnary_reserveModalTitle')}</DialogHeader>
        <DialogContent>
          <Formik
            initialValues={
              (data && reserveId) || originalReserve ? merge({}, initialValues, originalReserve || data) : initialValues
            }
            onSubmit={checkIfRentNeeded}
            enableReinitialize
          >
            {(formikProps) => {
              formSubmitting = formikProps.isSubmitting;
              return (
                <FormWithoutEnterSubmit id={t(formKey)}>
                  <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                      <FormTextField
                        name="title"
                        label={t('reserve_title')}
                        tooltipContent={t('tooltip_reserves_title')}
                        maxLength={50}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormSelectField
                        name={`bucket`}
                        label={t('general_portfolio')}
                        options={options.bucket.map((bucket) => ({
                          value: bucket,
                          label: t(`finQuestionnary_reserve_bucket_${bucket}`),
                        }))}
                        fast={false}
                        tooltipContent={t('tooltip_reserves_bucket')}
                      />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormNumberField
                        label={t('finQuestionnary_reserve_reserveValue')}
                        name={`value.value`}
                        endAdornment={translatedCurrency}
                        fast={false}
                      />
                      <TextField
                        name={`value.currency`}
                        type="hidden"
                        style={{ visibility: 'hidden' }}
                        value={currency}
                      />
                    </Grid>

                    <Grid item xs={6} md={4}>
                      <FormSelectField
                        name={`horizon`}
                        label={t('finQuestionnary_reserve_timeHorizon')}
                        options={horizonOptions}
                        fast={false}
                        tooltipContent={t('tooltip_reserves_timeHorizon')}
                      />
                    </Grid>

                    {formikProps.values.horizon === Horizon.PERIOD && (
                      <Grid item xs={6} md={4}>
                        <FormDateSelects name={`until`} minDate={minDate} maxDate={maxDate} fast={false} />
                      </Grid>
                    )}
                  </Grid>
                  <Grid container spacing={4} alignItems="center">
                    <Grid item xs={12} md={6}>
                      <Switch
                        name={`newDeposits`}
                        label={t('reserve_newDeposits')}
                        tooltipContent={
                          <span
                            dangerouslySetInnerHTML={{
                              __html: t(`tooltip_reserves_newDeposits`, {
                                interpolation: { escapeValue: false },
                              }),
                            }}
                          />
                        }
                        afterChange={() => {
                          formikProps.values.newDeposits === false && setNoNewDepositsDialogOpen(true);
                        }}
                      />
                    </Grid>
                  </Grid>

                  <Link
                    className={classes.detailLink}
                    onClick={(e: React.MouseEvent) => {
                      e.preventDefault();
                      setShowDetail(!showDetail);
                    }}
                  >
                    <span className={classes.detailControl}>
                      {t('general_advanced')}
                      <DropDownIcon className={`${showDetail ? classes.shownIcon : ''}`} />
                    </span>
                  </Link>

                  <Collapse in={showDetail}>
                    <Grid container>
                      <Grid container item xs={12} spacing={0} />
                      <Grid container item xs={12} spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Switch
                            name={`cancellationLimitActive`}
                            label={t('reserve_limit')}
                            tooltipContent={t('tooltip_reserves_cancellationLimit')}
                          />
                        </Grid>
                        {formikProps.values.cancellationLimitActive && (
                          <>
                            <Grid item xs={6} md={4}>
                              <Typography>{t('reserve_limitWhen')}</Typography>
                            </Grid>
                            <Grid item xs={6} md={2} style={{ paddingLeft: '8px' }}>
                              <FormSelectField
                                name={`cancellationLimit`}
                                label={t('general_value')}
                                options={new Array(options.maxLimit - options.minLimit + 1).fill(0).map((_p, i) => ({
                                  value: `${(i + options.minLimit) / 100}`,
                                  label: `${i + options.minLimit} %`,
                                }))}
                                fast={false}
                                tooltipContent={t('tooltip_reserves_cancellationValue')}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Grid>
                  </Collapse>

                  <ConfirmDialog
                    noHeader
                    isOpen={noNewDepositsDialogOpen}
                    confirm={() => setNoNewDepositsDialogOpen(false)}
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: t('finQuestionnary_reserve_noNewDeposits_info', {
                          interpolation: { escapeValue: false },
                        }),
                      }}
                    />
                  </ConfirmDialog>

                  <ConfirmDialog
                    noHeader
                    isOpen={rentDialogOpen}
                    confirm={async () => {
                      setRentDialogOpen(false);
                      const validated = await onSubmit(formikProps.values, formikProps);
                      if (validated && openRent) openRent(true);
                    }}
                    close={() => setRentDialogOpen(false)}
                    btnCloseText="finQuestionnary_reserve_noRent_changeHorizon"
                    btnConfirmText="finQuestionnary_reserve_noRent_setRent"
                  >
                    {t('finQuestionnary_reserve_noRent_info')}{' '}
                  </ConfirmDialog>
                </FormWithoutEnterSubmit>
              );
            }}
          </Formik>
        </DialogContent>
        <DialogActions>
          <ControlButtons
            isSubmitting={formSubmitting}
            nextText={t('general_save')}
            form={formKey}
            nextButtonId="reserve-modal-submit"
            backText={t('general_cancel')}
            onBack={() => handleClose(false)}
            onClick={() => {}}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default compose(observer)(ReserveModal);
